import React, { useEffect, useState } from "react";
import BakeryDiningOutlinedIcon from "@mui/icons-material/BakeryDiningOutlined";
import RightworkTimePicker from "../../../components/shared/RightworkTimePicker";
import PrepHourlyChart from "./PrepHourlyChart";
import usePrepHistory from "./usePrepHistory";

export const parseISODate = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
};

export const shiftISODate = (iso, days) => {
  const next = parseISODate(iso);
  next.setDate(next.getDate() + days);
  const pad = n => String(n).padStart(2, "0");
  return `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}`;
};

const formatDay = (iso, options) => parseISODate(iso).toLocaleDateString(undefined, options);
const formatNumber = (value, digits = 0) =>
  value == null ? "—" : Number(value).toLocaleString(undefined, { maximumFractionDigits: digits });

const formatTime = (hhmm) => {
  const [hours, minutes] = hhmm.split(":").map(Number);
  return new Date(2000, 0, 1, hours, minutes).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};
const pullDeadline = (item) => {
  const day = formatDay(item.pull_on, { weekday: "long" });
  return item.pull_by ? `${formatTime(item.pull_by)} ${day}` : day;
};

// A box that saves whole numbers when it loses focus. Empty clears the value.
function SaveOnBlurInput({ label, value, onSave, locked, placeholder }) {
  const initial = value == null ? "" : String(value);
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => setDraft(initial), [initial]);

  const commit = async () => {
    const next = draft.trim();
    if (next === initial) return;
    if (next !== "" && !/^\d+$/.test(next)) {
      setDraft(initial);
      return;
    }
    setSaving(true);
    const saved = await onSave(next === "" ? null : Number(next));
    setSaving(false);
    if (!saved) setDraft(initial);
  };

  return <input
    aria-label={label}
    inputMode="numeric"
    value={draft}
    placeholder={placeholder}
    disabled={saving || locked}
    onChange={event => setDraft(event.target.value)}
    onBlur={commit}
    onKeyDown={event => { if (event.key === "Enter") event.currentTarget.blur(); }}
    className="h-9 w-24 rounded-lg border border-slate-300 bg-white px-2.5 text-right text-sm tabular-nums outline-none focus:border-violet-700 focus:ring-1 focus:ring-violet-700 disabled:opacity-60"
  />;
}

function OnHandInput({ item, forDate, onSave, locked }) {
  return (
    <div className="flex flex-col items-end gap-1">
      <SaveOnBlurInput label={`${item.name} on hand`} value={item.on_hand} placeholder="Count" locked={locked}
        onSave={quantity => onSave(item.uuid, forDate, quantity)} />
      {item.counted_by && <span className="text-[11px] text-slate-400">
        {item.counted_by}{item.counted_at && ` · ${new Date(item.counted_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`}
      </span>}
    </div>
  );
}

const resultKey = (date, uuid) => `${date}:${uuid}`;
const savedFields = (item) => ({ wasted: item.wasted == null ? "" : String(item.wasted), sold_out_at: item.sold_out_at || null });

function RecentResults({ plan, shownItemIds, onSave, onDirtyChange, locked }) {
  const days = plan.recent_results || [];
  // Edits wait here until their day is saved, keyed by day and item so they
  // survive the plan being replaced by another day's save.
  const [edits, setEdits] = useState({});
  const [savingDates, setSavingDates] = useState({});
  const dirty = Object.keys(edits).length > 0;

  useEffect(() => {
    onDirtyChange?.(dirty);
    if (!dirty) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  if (days.length === 0) return null;
  const columns = days[0].items.filter(item => shownItemIds.has(item.uuid)
    || days.some(day => day.items.some(logged => logged.uuid === item.uuid && (logged.wasted != null || logged.sold_out_at))));
  const dayLabel = iso => formatDay(iso, { weekday: "short", month: "short", day: "numeric" });
  const fieldsFor = (day, item) => ({ ...savedFields(item), ...edits[resultKey(day.date, item.uuid)] });

  // Holds only the fields the manager changed, so a plan refreshed with another
  // manager's newer value never reads as an edit here.
  const edit = (day, item, field, value) => setEdits(previous => {
    const key = resultKey(day.date, item.uuid);
    const { [field]: _old, ...otherFields } = previous[key] || {};
    const next = value === savedFields(item)[field] ? otherFields : { ...otherFields, [field]: value };
    const { [key]: _removed, ...others } = previous;
    return Object.keys(next).length === 0 ? others : { ...others, [key]: next };
  });

  const save = async (day) => {
    const changed = day.items.filter(item => edits[resultKey(day.date, item.uuid)]);
    setSavingDates(previous => ({ ...previous, [day.date]: true }));
    const saved = await onSave(day.date, plan.target_date, changed.map(item => {
      const changes = edits[resultKey(day.date, item.uuid)];
      return {
        prep_item_uuid: item.uuid,
        ...("wasted" in changes && { wasted: changes.wasted === "" ? null : Number(changes.wasted) }),
        ...("sold_out_at" in changes && { sold_out_at: changes.sold_out_at }),
      };
    }));
    setSavingDates(({ [day.date]: _done, ...others }) => others);
    if (saved) {
      setEdits(previous => Object.fromEntries(Object.entries(previous).filter(([key]) => !changed.some(item => key === resultKey(day.date, item.uuid)))));
    }
  };

  return <section className="overflow-hidden rounded-xl border border-slate-200" aria-label="Waste and sell-outs">
    <div className="border-b border-slate-200 px-5 py-4">
      <h3 className="font-semibold">Waste and sell-outs</h3>
      <p className="mt-1 text-xs text-slate-500">Log what was thrown out and when an item ran out. Leave “Sold out at” blank if it lasted the whole day.</p>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-left text-sm">
        <thead className="bg-slate-50 text-xs font-semibold text-slate-500"><tr>
          <th className="px-5 py-3" scope="col">Day</th>
          <th className="px-5 py-3" scope="col">Item</th>
          <th className="px-5 py-3 text-right" scope="col">Wasted</th>
          <th className="px-5 py-3 text-right" scope="col">Sold out at</th>
          <th className="px-5 py-3" scope="col"><span className="sr-only">Save</span></th>
        </tr></thead>
        {days.map(day => {
          const dayItems = columns.map(column => day.items.find(item => item.uuid === column.uuid)).filter(Boolean);
          const dayDirty = day.items.some(item => edits[resultKey(day.date, item.uuid)]);
          const saving = Boolean(savingDates[day.date]);
          const savers = [...new Set(day.items.map(item => item.updated_by).filter(Boolean))];
          return <tbody key={day.date} className="border-t border-slate-200 first:border-t-0">{dayItems.map((item, index) => {
            const fields = fieldsFor(day, item);
            return <tr key={item.uuid} className={index > 0 ? "border-t border-slate-100" : ""}>
              {index === 0 && <th className="px-5 py-3 align-top font-normal whitespace-nowrap text-slate-600" scope="rowgroup" rowSpan={dayItems.length}>
                <span className="inline-flex h-9 items-center">{dayLabel(day.date)}</span>
              </th>}
              <td className="px-5 py-3 font-medium">{item.name}</td>
              <td className="px-5 py-3 text-right">
                <input aria-label={`${item.name} wasted on ${dayLabel(day.date)}`} inputMode="numeric" value={fields.wasted} disabled={saving || locked}
                  onChange={event => edit(day, item, "wasted", event.target.value.replace(/\D/g, ""))}
                  className="h-9 w-24 rounded-lg border border-slate-300 bg-white px-2.5 text-right text-sm tabular-nums outline-none focus:border-violet-700 focus:ring-1 focus:ring-violet-700 disabled:opacity-60" />
              </td>
              <td className="px-5 py-3"><div className="flex justify-end">
                <RightworkTimePicker clearable label={`${item.name} sold out on ${dayLabel(day.date)}`} value={fields.sold_out_at} disabled={saving || locked}
                  onChange={time => edit(day, item, "sold_out_at", time)} />
              </div></td>
              {index === 0 && <td className="px-5 py-3 align-top text-right whitespace-nowrap" rowSpan={dayItems.length}>
                <button type="button" disabled={!dayDirty || saving || locked} onClick={() => save(day)}
                  aria-label={`Save ${dayLabel(day.date)}`}
                  className="h-9 rounded-lg bg-violet-700 px-3 text-sm font-semibold text-white hover:bg-violet-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-700 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400">
                  {saving ? "Saving…" : "Save"}
                </button>
                {!dayDirty && !saving && savers.length > 0 && <p className="mt-1 text-[11px] text-slate-400">Saved by {savers.join(", ")}</p>}
              </td>}
            </tr>;
          })}</tbody>;
        })}
      </table>
    </div>
  </section>;
}

function PullHistory({ homeStore, shownItemIds, refreshKey, mixedUnits }) {
  const { days, loading, error } = usePrepHistory(homeStore, refreshKey);
  const dayLabel = iso => formatDay(iso, { weekday: "short", month: "short", day: "numeric" });
  const shown = (days || [])
    .map(day => ({ ...day, items: day.items.filter(item => shownItemIds.has(item.uuid) || item.sold > 0 || item.wasted != null || item.sold_out_at) }))
    .filter(day => day.items.length > 0);
  const rows = shown.flatMap(day => day.items);
  const sum = (values) => values.reduce((total, value) => total + (value || 0), 0);
  // A day with no suggestion can't be compared, so neither can the week.
  const incomplete = rows.some(item => item.suggested_pull == null);
  const totals = { suggested: incomplete ? null : sum(rows.map(item => item.suggested_pull)), sold: sum(rows.map(item => item.sold)), wasted: sum(rows.map(item => item.wasted)) };

  return <section className="overflow-hidden rounded-xl border border-slate-200" aria-label="How pulls went">
    <div className="border-b border-slate-200 px-5 py-4">
      <h3 className="font-semibold">How pulls went</h3>
      <p className="mt-1 text-xs text-slate-500">The last 7 days. “Left over” assumes the suggested pull was used, since actual pulls aren’t recorded.</p>
    </div>
    {days == null && loading && <p role="status" className="px-5 py-6 text-sm text-slate-500">Loading the last 7 days…</p>}
    {error && <p role="alert" className="px-5 py-6 text-sm text-amber-800">{days == null ? "Couldn’t load the last 7 days." : "Couldn’t update the last 7 days, so this may be out of date."} Refresh to try again.</p>}
    {days != null && shown.length === 0 && <p className="px-5 py-6 text-sm text-slate-500">No sales in the last 7 days.</p>}
    {shown.length > 0 && <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-left text-sm">
        <thead className="bg-slate-50 text-xs font-semibold text-slate-500"><tr>
          <th className="px-5 py-3" scope="col">Day</th>
          <th className="px-5 py-3" scope="col">Item</th>
          <th className="px-5 py-3 text-right" scope="col">Suggested pull</th>
          <th className="px-5 py-3 text-right" scope="col">Sold</th>
          <th className="px-5 py-3 text-right" scope="col">Left over</th>
          <th className="px-5 py-3 text-right" scope="col">Wasted</th>
          <th className="px-5 py-3 text-right" scope="col">Sold out at</th>
        </tr></thead>
        {shown.map(day => <tbody key={day.date} className="border-t border-slate-200 first:border-t-0">{day.items.map((item, index) => {
          const leftOver = item.suggested_pull == null ? null : Math.round(item.suggested_pull + (item.on_hand || 0) - item.sold);
          return <tr key={item.uuid} className={index > 0 ? "border-t border-slate-100" : ""}>
            {index === 0 && <th className="px-5 py-3 align-top font-normal whitespace-nowrap text-slate-600" scope="rowgroup" rowSpan={day.items.length}>{dayLabel(day.date)}</th>}
            <td className="px-5 py-3 font-medium">{item.name}</td>
            <td className="px-5 py-3 text-right tabular-nums">{formatNumber(item.suggested_pull)}</td>
            <td className="px-5 py-3 text-right tabular-nums">{formatNumber(item.sold)}</td>
            <td className="px-5 py-3 text-right tabular-nums">{leftOver == null ? "—" : leftOver < 0
              ? <span className="font-semibold text-amber-800">Short {formatNumber(-leftOver)}</span> : formatNumber(leftOver)}</td>
            <td className="px-5 py-3 text-right tabular-nums">{formatNumber(item.wasted)}</td>
            <td className="px-5 py-3 text-right tabular-nums">{item.sold_out_at ? formatTime(item.sold_out_at) : "—"}</td>
          </tr>;
        })}</tbody>)}
        {/* Different units don't add up. */}
        {!mixedUnits && <tfoot className="border-t border-slate-200 bg-slate-50 font-semibold"><tr>
          <th className="px-5 py-3" scope="row" colSpan={2}>Last 7 days</th>
          <td className="px-5 py-3 text-right tabular-nums">{formatNumber(totals.suggested)}</td>
          <td className="px-5 py-3 text-right tabular-nums">{formatNumber(totals.sold)}</td>
          <td className="px-5 py-3 text-right tabular-nums">{incomplete ? "—" : formatNumber(Math.round(totals.suggested + sum(rows.map(item => item.on_hand)) - totals.sold))}</td>
          <td className="px-5 py-3 text-right tabular-nums">{formatNumber(totals.wasted)}</td>
          <td />
        </tr></tfoot>}
      </table>
    </div>}
  </section>;
}

// An item with nothing sold on recent same weekdays and nothing to pull has no
// place at this location, like shareable dough at a store that only sells solo.
const hasRecentActivity = (item) =>
  !item.source_label || item.pull > 0 || item.last_4.some(day => day.value > 0);

export default function PrepPlanDisplay({ prepPlan, internal, onDirtyChange, homeStore }) {
  const { plan, loading, error, reload, saveCount, saveResults } = prepPlan;

  if (!plan) {
    if (loading) return <p role="status" className="py-12 text-center text-sm text-slate-500">Loading the prep plan…</p>;
    return <div role={error ? "alert" : undefined} className="rounded-xl border border-slate-200 px-6 py-12 text-center">
      <BakeryDiningOutlinedIcon className="text-slate-400" sx={{ fontSize: 32 }} />
      <h3 className="mt-3 font-semibold">{error ? "The prep plan didn’t load" : "No prep items at this location"}</h3>
      <p className="mt-2 text-sm text-slate-500">{error || "Prep items are set up by Rightwork for your company."}</p>
      {error && <button type="button" onClick={reload}
        className="mt-4 rounded-lg bg-violet-700 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-700">
        Try again
      </button>}
    </div>;
  }

  const active = plan.items.filter(hasRecentActivity);
  // If nothing has recent sales, show everything rather than an empty page.
  const items = active.length > 0 ? active : plan.items;
  const linked = items.filter(item => item.source_label);
  const units = [...new Set(linked.map(item => item.unit_label))];
  // Unknown pulls must not read as "pull 0", and different units don't add up.
  const mixedUnits = units.length > 1;
  const totalPull = mixedUnits || linked.some(item => item.pull == null) ? null : linked.reduce((sum, item) => sum + item.pull, 0);
  const noForecast = plan.revenue_forecast == null;
  const missingHistory = !noForecast && plan.items.some(item => item.source_label && item.pull == null);
  const counting = linked.filter(item => item.tracks_on_hand);
  const counted = counting.filter(item => item.on_hand != null).length;
  const showOnHand = counting.length > 0;
  const deadlines = [...new Set(linked.map(pullDeadline))];
  const adjustment = Math.round((plan.adjustment_percent || 0) * 100);
  const weekday = formatDay(plan.target_date, { weekday: "long" });

  const tiles = [
    { label: `Forecast sales · ${weekday}`, value: plan.revenue_forecast == null ? "—" : `$${formatNumber(plan.revenue_forecast)}`,
      detail: adjustment ? `Includes a ${adjustment > 0 ? "+" : ""}${adjustment}% forecast adjustment` : "Rightwork sales forecast" },
    { label: "Total to pull", value: formatNumber(totalPull),
      detail: mixedUnits ? "Mixed units, see each item" : noForecast ? "Needs a sales forecast for this day"
        : totalPull == null ? "Some items have no sales history" : units[0] || "Across all prep items" },
    showOnHand
      ? { label: "Counted on hand", value: `${counted} of ${counting.length}`,
          detail: counted === counting.length ? "Every item has a count" : "Pull assumes nothing on hand until counted" }
      : { label: "Pull by", proportional: true, value: deadlines.length === 1 ? deadlines[0] : "—",
          detail: deadlines.length > 1 ? "Differs by item, see each item" : `For ${weekday}` },
  ];

  return <>
    {error && <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
      {error} Showing the last plan that loaded.
    </div>}
    <div>
      <h2 className="text-lg font-semibold">Prep for {formatDay(plan.target_date, { weekday: "long", month: "long", day: "numeric" })}</h2>
      <p className="mt-1 text-sm text-slate-500">{showOnHand ? "Pull = forecast + buffer − what’s already on hand. Enter tonight’s count to update it." : "Pull = forecast + buffer."}</p>
      {noForecast && linked.length > 0 && <p className="mt-2 text-sm text-amber-800">There’s no sales forecast for this day yet, so pulls can’t be calculated. Forecasts are usually ready a few weeks ahead.</p>}
      {missingHistory && <p className="mt-2 text-sm text-amber-800">Some items have no recent sales history, so their pull can’t be calculated. Ask Rightwork to check how the item is set up.</p>}
    </div>

    <div className="grid grid-cols-3 gap-4 sm:grid-cols-1" aria-label="Prep summary">
      {tiles.map(tile => <div key={tile.label} className="rounded-xl border border-slate-200 bg-white p-5">
        <p className="text-sm font-medium text-slate-600">{tile.label}</p>
        <p className={`mt-3 text-3xl font-semibold text-slate-900 ${tile.proportional ? "" : "tabular-nums"}`}>{tile.value}</p>
        <p className="mt-2 text-xs text-slate-500">{tile.detail}</p>
      </div>)}
    </div>

    <section className="overflow-hidden rounded-xl border border-slate-200" aria-label="Pull by item">
      <div className="border-b border-slate-200 px-5 py-4"><h3 className="font-semibold">Pull by item</h3></div>
      <p className="hidden border-b border-slate-200 px-5 py-2 text-xs text-slate-500 sm:block">Scroll horizontally to view all columns.</p>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead className="bg-slate-50 text-xs font-semibold text-slate-500"><tr>
            <th className="px-5 py-3" scope="col">Item</th>
            <th className="px-5 py-3 text-right" scope="col">Forecast</th>
            <th className="px-5 py-3 text-right" scope="col">Needed</th>
            <th className="px-5 py-3 text-right" scope="col">Buffer</th>
            {showOnHand && <th className="px-5 py-3 text-right" scope="col">On hand</th>}
            <th className="px-5 py-3 text-right" scope="col">Pull</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">{items.map(item => <tr key={item.uuid}>
            <td className="px-5 py-4">
              <div className="flex items-center gap-2 font-medium">{item.name}
                {internal && !item.is_active && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">Inactive · staff only</span>}
              </div>
              {/* The tiles carry the unit and deadline when every item shares them. */}
              <div className="mt-1 text-xs text-slate-500">{item.source_label ? `${mixedUnits ? `${item.unit_label} · ` : ""}Includes ${item.source_label}` : "Not set up yet"}</div>
              {item.source_label && (showOnHand || deadlines.length > 1) && <div className="mt-0.5 text-xs text-slate-500">Pull by {pullDeadline(item)}</div>}
            </td>
            {item.source_label ? <>
              <td className="px-5 py-4 text-right tabular-nums">{formatNumber(item.forecast, 1)}</td>
              <td className="px-5 py-4 text-right tabular-nums">{formatNumber(item.needed)}</td>
              <td className="px-5 py-4 text-right tabular-nums text-slate-600">{item.buffer == null ? "—" : `+${formatNumber(item.buffer)}`} <span className="text-xs text-slate-400">({formatNumber(item.buffer_percent, 1)}%)</span></td>
              {showOnHand && <td className="px-5 py-3 text-right">{/* Keyed per day so a save still finishing from another day can't touch this box;
                locked while a plan loads so the day being replaced can't be edited. */}
                {item.tracks_on_hand
                  ? <OnHandInput key={`${item.uuid}:${plan.target_date}`} item={item} forDate={plan.target_date} onSave={saveCount} locked={loading} />
                  : <span className="text-slate-400">—</span>}</td>}
              <td className="px-5 py-4 text-right text-lg font-semibold tabular-nums text-violet-700">{formatNumber(item.pull)}</td>
            </> : <td colSpan={showOnHand ? 5 : 4} className="px-5 py-4 text-right text-sm text-slate-400">Ask Rightwork to finish setting up this item.</td>}
          </tr>)}</tbody>
        </table>
      </div>
    </section>

    {/* Keyed by location: prep items are shared across a company, so a draft must never carry over to another store. */}
    <RecentResults key={prepPlan.key} plan={plan} shownItemIds={new Set(items.map(item => item.uuid))} onSave={saveResults} onDirtyChange={onDirtyChange} locked={loading} />

    <PullHistory homeStore={homeStore} mixedUnits={new Set(plan.items.filter(item => item.source_label).map(item => item.unit_label)).size > 1} shownItemIds={new Set(items.map(item => item.uuid))} refreshKey={JSON.stringify([plan.recent_results || [], plan.items.map(item => item.on_hand)])} />

    <div className="grid grid-cols-3 gap-4 lg:grid-cols-1">
      <section className="col-span-2 rounded-xl border border-slate-200 p-5 lg:col-span-1" aria-label="Forecast by hour">
        <h3 className="font-semibold">Forecast by hour · {weekday}</h3>
        <p className="mt-1 text-xs text-slate-500">When the forecast says it will sell, to help time a second pull.</p>
        <div className="mt-4"><PrepHourlyChart items={linked} /></div>
      </section>
      <section className="rounded-xl border border-slate-200 p-5" aria-label="Last four weeks">
        <h3 className="font-semibold">Last 4 {weekday}s</h3>
        <p className="mt-1 text-xs text-slate-500">What past sales actually used, to sanity-check the forecast.</p>
        <div className="mt-4 space-y-4">{linked.map(item => <div key={item.uuid}>
          <p className="text-sm font-medium">{item.name}</p>
          <ul className="mt-1.5 space-y-1 text-sm">{item.last_4.map(day => <li key={day.date} className="flex justify-between text-slate-600">
            <span>{formatDay(day.date, { month: "short", day: "numeric" })}</span>
            <span className="tabular-nums">{formatNumber(day.value)}</span>
          </li>)}</ul>
        </div>)}</div>
      </section>
    </div>
  </>;
}
